import React, { useCallback, useState } from 'react';

import PropTypes from 'prop-types';
import { MdDelete, MdEdit, MdFileCopy, MdMoreVert } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { ALLOW_DELETE_ACTIVITIES } from '@config';
import {
  goToCalendarEditActivity,
  hasNewPermissionSel,
  offlineSel,
} from '@shared';
import { Button, Dropdown, Menu } from '@shared/components';
import { newPermissions } from '@shared/newPermissions';

import { DeleteActivityButton } from '../../../common';
import styles from './ActivityActionsMenu.less';

const { Item: MenuItem } = Menu;

const ActivityActionsMenu = ({
  activityId,
  hideEdit,
  hideDuplicate,
  hideDelete,
  ...props
}) => {
  const dispatch = useDispatch();
  const [isVisible, setIsVisible] = useState(false);
  const isOffline = useSelector(offlineSel);
  const canEdit = useSelector(state =>
    hasNewPermissionSel(
      state,
      newPermissions.CRUD_ACTIVITIES,
      newPermissions.CRUD_SUB_ACTIVITIES,
    ),
  );

  const handleVisibleChange = useCallback(
    visible => {
      setIsVisible(visible);
    },
    [setIsVisible],
  );

  const handleItemClick = useCallback(
    (clone = false) =>
      () => {
        dispatch(
          goToCalendarEditActivity({
            activityId,
            clone,
          }),
        );
      },
    [dispatch, activityId],
  );

  const handleDeleteToggle = event => {
    event.stopPropagation();
    setIsVisible(false);
  };

  if (!canEdit || (hideEdit && hideDuplicate && hideDelete)) {
    return null;
  }

  return (
    <Dropdown
      onVisibleChange={handleVisibleChange}
      visible={isVisible}
      overlay={
        <Menu className={styles.actionsMenu}>
          {!hideEdit && (
            <MenuItem>
              <Button
                disabled={isOffline}
                onClick={handleItemClick()}
                type="link"
                size="small"
                block
              >
                <MdEdit /> <FormattedMessage id="shared.edit" />
              </Button>
            </MenuItem>
          )}
          {!hideDuplicate && (
            <MenuItem>
              <Button
                disabled={isOffline}
                onClick={handleItemClick(true)}
                type="link"
                size="small"
                block
              >
                <MdFileCopy /> <FormattedMessage id="shared.duplicte" />
              </Button>
            </MenuItem>
          )}
          {ALLOW_DELETE_ACTIVITIES && canEdit && !hideDelete && (
            <MenuItem>
              <div
                className={styles.deleteButton}
                onClick={event => handleDeleteToggle(event)}
              >
                <DeleteActivityButton
                  id={activityId}
                  askForConfirmation
                  size="small"
                  showTooltip={false}
                  className={styles.deleteButton}
                >
                  <MdDelete /> <FormattedMessage id="shared.delete" />
                </DeleteActivityButton>
              </div>
            </MenuItem>
          )}
        </Menu>
      }
      trigger={['click']}
      placement="bottomRight"
      {...props}
    >
      <span className={styles.moreIcon}>
        <MdMoreVert />
      </span>
    </Dropdown>
  );
};

ActivityActionsMenu.propTypes = {
  activityId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onLoadCallback: PropTypes.func,
  onItemClick: PropTypes.func,
  hideEdit: PropTypes.bool,
  hideDuplicate: PropTypes.bool,
  hideDelete: PropTypes.bool,
};

ActivityActionsMenu.defaultProps = {
  onLoadCallback: () => {},
  onItemClick: () => {},
};

export default ActivityActionsMenu;
